import type { ReactElement, ReactNode } from 'react';

import styles from './AppShell.module.css';
import { useVirtualKeyboard } from './useVirtualKeyboard';

/**
 * The application frame — docs/ARCHITECTURE.md §3, docs/DESIGN.md §8, ADR-0101 §6.1, recon §1.1.
 *
 * ═══ THE ONE RULE THIS FILE EXISTS TO ENFORCE ═══
 *
 * `height: 100dvh`, and a grid whose view row is `minmax(0, 1fr)`. NO VIEWPORT ARITHMETIC.
 *
 * The legacy shell sized its editor with
 * `h-[calc(100vh-108px)] sm:h-[calc(100vh-104px)] md:h-[calc(100vh-88px)]` — three magic
 * numbers that had to be re-derived by hand whenever a bar changed height, and that were
 * already wrong on every mobile browser with a collapsing URL bar, because `100vh` there is
 * the LARGEST viewport and not the current one. The page overflowed by exactly the height of
 * the URL bar, and the bottom of the editor sat underneath it.
 *
 * Here the rows are declared rather than computed: the header takes `--bar-top`, the dock and
 * the tab strip take their intrinsic heights, and everything left over goes to the view.
 * Changing `--bar-top` changes the layout and nothing else has to be told.
 *
 * `minmax(0, 1fr)` and not `1fr`: a grid item's automatic minimum size is its CONTENT, so a
 * plain `1fr` row holding a 5000-line document grows past the viewport and the whole page
 * scrolls. The `0` minimum is what puts the overflow inside the editor instead — which is
 * also what keeps the toolbar on screen while the document is scrolled.
 *
 * ═══ THE WORKSPACE WRAPPER, AND WHY IT IS USUALLY NOT A BOX ═══
 *
 * ADR-0101 §6.2 asks for two different shapes out of one DOM. Below 960px the toolbar is a
 * DOCK pinned to the bottom of the phone and the view region alone is the rounded card; from
 * 960px the toolbar is the card's first row, above the editor, inside the same rounded edge.
 *
 * `.workspace` is `display: contents` below 960 — it draws nothing and its three children
 * become grid items of `.app` directly, so `grid-area: dock` can put the toolbar in the last
 * row while it stays the SECOND child in the DOM. From 960 the wrapper stops being invisible
 * and becomes the card itself, and the same three children lay out in source order inside it.
 * No prop, no branch, no second template: one DOM, two stylesheets' worth of placement.
 *
 * ═══ THE DELIBERATE MISMATCH BETWEEN PAINT ORDER AND DOM ORDER ═══
 *
 * On a phone the dock is drawn last and focused second. ADR-0101 §6.1 records that on purpose:
 * the toolbar still PRECEDES the content it acts on for a screen reader and for Tab, which is
 * the order that means something, and the skip link jumps past all of it straight to
 * `#editor`. The alternative — moving the toolbar after `<main>` in the DOM — would put seven
 * actions behind a whole document for every keyboard user, to fix a mismatch nobody can hear.
 *
 * ═══ WHY THE TAB STRIP STILL HAS A ROW OF ITS OWN ═══
 *
 * `TabBar` returns `null` while one view is registered (ADR-0020), so the caller hands this
 * component `<TabBar …/>`: a perfectly truthy React element that renders nothing. No
 * inspection of the prop can tell the two cases apart — the row count is a child's runtime
 * decision, not something this box can see. So the template declares a `tabs` area between the
 * view and the dock and lets the strip auto-place into it; unclaimed, that row is 0px, because
 * this grid has no `gap`. Without it an auto-placed strip would land BELOW the dock in exactly
 * the configuration a second view would ship.
 *
 * ═══ WHAT IS DELIBERATELY NOT A SLOT ═══
 *
 * The repair banner lives inside `children`, and the toast region inside the editor pane
 * (docs/DESIGN.md §8, ADR-0101 §7.10: on phones the toast docks to the card's bottom edge and
 * overlays the code rather than displacing it — mounted at the root, that rule cannot work).
 * Dialogs are siblings of the shell: `<dialog>` opened with `showModal()` renders in the top
 * layer and has no business being a grid item.
 */

export interface AppShellProps {
  /**
   * Rendered first so it is the first thing `Tab` reaches. It MUST be out of flow
   * (`SkipLink` is `position: fixed`); an in-flow child here would take the header's row.
   */
  readonly skipLink?: ReactNode;
  readonly header: ReactNode;
  readonly toolbar: ReactNode;
  /**
   * The tab strip. Pass `<TabBar …/>` unconditionally: it renders `null` while one view is
   * registered (ADR-0020) and the layout does not care either way — see the note above on why
   * this component never asks whether the slot produced anything, and why the grid keeps a
   * zero-height row for it regardless.
   */
  readonly tabs?: ReactNode;
  /** The view region. Scrolls internally; never grows the page. */
  readonly children: ReactNode;
  /** Localised, `a11y.editor`. Names the `<main>` landmark. */
  readonly mainLabel: string;
  /** The `<main>` id, and the skip link's target. */
  readonly mainId?: string;
}

export function AppShell({
  skipLink,
  header,
  toolbar,
  tabs,
  children,
  mainLabel,
  mainId = 'editor',
}: AppShellProps): ReactElement {
  /*
    The one JS-driven layout change in the product (ADR-0101 §6.4). It writes
    `:root[data-keyboard]`, which `Toolbar.module.css` and `Drawer.module.css` read to get the
    dock and the diagnostics sheet out from under a phone's soft keyboard. It is called here
    rather than in the composition root because the two boxes it moves are rows of THIS grid,
    and because the frame is the only component that is mounted exactly once.
  */
  useVirtualKeyboard();

  return (
    <div className={styles.app}>
      {skipLink}
      {header}
      {/*
        `data-surface="card"` rather than a class: under forced colours every tinted surface
        loses the tone that separated it and has to ask for a `CanvasText` edge back, and
        base.css states that rule once for `[data-surface]`. It applies from 960px, where this
        wrapper is the card; below that the wrapper is `display: contents` and the card is
        `.main`, which draws its own forced-colours border.
      */}
      <div className={styles.workspace} data-surface="card">
        {toolbar}
        {tabs}
        {/*
          `tabIndex={-1}` is here for the skip link: an anchor pointing at a non-focusable
          element moves the scroll position but not the focus, so the next Tab continues from
          the link rather than from the editor — a skip link that does not skip.
        */}
        <main id={mainId} className={styles.main} aria-label={mainLabel} tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}

export default AppShell;
